import { useEffect, useRef, useState } from 'react';

import { useGetParams } from './hooks/useGetParams';
import { useBroadcast } from './hooks/useBroadcast';
import { useNoiseLevel } from './hooks/useNoiseLevel';

import { Logos } from './components/Logos/Logos';
import { PrevRecord } from './components/PrevRecord/PrevRecord';
import { NoizeValue } from './components/NoizeValue/NoizeValue';
import { NoizeMeter } from './components/NoizeMeter/NoizeMeter';
import { NoizeScale } from './components/NoizeScale/NoizeScale';
import { Player } from './components/Player/Player';

import defaultBackground from './assets/images/bg.png';
import { usePrevRecord } from './hooks/usePrevRecord';

const maxRecordState = 'S12';
const microphonMuterState = 'S13';

function App() {
  const { currentState, allStates, prevRecord } = useBroadcast();

  const prevDeltaRef = useRef(0);
  const [remSize, setRemSize] = useState(16);

  useEffect(() => {
    const updateRemSize = () => {
      setRemSize(
        parseFloat(getComputedStyle(document.documentElement).fontSize),
      );
    };

    updateRemSize();
    window.addEventListener('resize', updateRemSize);

    return () => window.removeEventListener('resize', updateRemSize);
  }, []);

  const {
    calibrationOffset,
    deltaDuration,
    smoothingFactor,
    maxNoiseLevel,
    bg,
  } = useGetParams();

  const {
    calibratedLevelDbSPL,
    currentMicrophone,
    addSmoothBoost,
    stopMeasurement,
    startMeasurement,
    isActiveMeasurement,
    startFake,
    stopFake,
    isActiveFake,
    maxCalibratedLevelDbSPL,
    setCalibratedLevelDbSPL,
  } = useNoiseLevel({
    calibrationOffset,
    deltaDuration,
    smoothingFactor,
  });

  const { setNoizeLevel } = usePrevRecord({
    calibratedLevelDbSPL,
    setCalibratedLevelDbSPL,
    prevRecord,
    deltaDuration,
  });

  useEffect(() => {
    if (currentState === microphonMuterState) {
      if (isActiveMeasurement) {
        stopMeasurement();
      }
    }

    if (currentState === microphonMuterState && !isActiveFake) {
      startFake();
      addSmoothBoost(-prevDeltaRef.current);
      prevDeltaRef.current = 0;
    } else if (currentState !== microphonMuterState) {
      if (isActiveFake) {
        stopFake();
      }
    }
  }, [currentState, isActiveFake, isActiveMeasurement]);

  useEffect(() => {
    if (currentState !== maxRecordState) {
      return;
    }

    if (isActiveMeasurement) {
      stopMeasurement();
    }

    setNoizeLevel(Number(maxCalibratedLevelDbSPL));
  }, [
    currentState,
    isActiveMeasurement,
    isActiveFake,
    maxCalibratedLevelDbSPL,
  ]);

  useEffect(() => {
    if (currentState !== 'S6') {
      return;
    }
    if (isActiveMeasurement) {
      stopMeasurement();
    }

    setNoizeLevel(prevRecord);
  }, [currentState, isActiveMeasurement, prevRecord]);

  useEffect(() => {
    const deltaStates = ['S1', 'S2', 'S3', 'S4', 'S5'];

    if (!deltaStates.includes(currentState)) {
      return;
    }

    if (!isActiveMeasurement) {
      startMeasurement();
    }

    if (allStates && currentState) {
      let deltaFromName = 0;

      if (currentState === 'S1') {
        deltaFromName = 0;
      } else {
        deltaFromName = Number(allStates[currentState]?.value || 0) || 0;
      }

      if (deltaFromName !== prevDeltaRef.current) {
        const delta = deltaFromName - prevDeltaRef.current;
        prevDeltaRef.current = deltaFromName;
        addSmoothBoost(delta);
      }
    }
  }, [currentState, allStates, addSmoothBoost, isActiveMeasurement]);

  useEffect(() => {
    if (currentMicrophone?.label) {
      console.log(currentMicrophone?.label);
    }
  }, [currentMicrophone]);

  const background =
    bg && bg !== '0'
      ? `url(${bg === '1' ? defaultBackground : bg}) center/cover no-repeat`
      : `none`;

  return (
    <div className="app-wrapper">
      <div className="titre-wrapper" style={{ background }}>
        <Logos />

        <div className="prevRecordBlock">
          <PrevRecord prevRecord={prevRecord} />
        </div>

        <div className="noize-wrapper">
          <NoizeValue noiseLevel={calibratedLevelDbSPL} />

          <NoizeMeter
            noiseLevel={calibratedLevelDbSPL}
            maxNoiseLevel={maxNoiseLevel}
            isNewRecord={currentState === maxRecordState}
          />

          <NoizeScale
            noiseLevel={calibratedLevelDbSPL}
            maxNoiseLevel={maxNoiseLevel}
            remSize={remSize}
          />
        </div>

        <div className="playerBlock">
          <Player currentState={currentState} allStates={allStates} />
        </div>
      </div>
    </div>
  );
}

export default App;
